import { useMemo } from 'react';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';
import { formatCurrency } from '../../shared/models';
import { earningsRange } from './useDeliveredOrders';

// iOS EarningsHistoryChart maxBars per filter.
const MAX_BARS = { Day: 1, Week: 7, Month: 31, Year: 12 };

const fmtDay = d => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
const fmtMonth = d => d.toLocaleDateString('en-US', { month: 'short' });
const fmtMonthYear = d => d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
const dayOfWeek = d => d.toLocaleDateString('en-US', { weekday: 'long' });

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

// "Earnings Overview" chart + daily/monthly breakdown list, mirroring iOS
// DriverEarningsView's overview card. `bare` drops the outer card chrome
// when the caller already renders one.
export default function EarningsHistorySection({ orders, timeFilter, bare = false }) {
  const entries = useMemo(() => {
    const [start, end] = earningsRange(timeFilter);
    const filtered = orders.filter(o => o.hasFee && o.date >= start && o.date <= end);
    const bucket = timeFilter === 'Year' ? startOfMonth : startOfDay;
    const grouped = new Map();
    for (const o of filtered) {
      const key = bucket(o.date).getTime();
      const g = grouped.get(key) || { date: bucket(o.date), totalEarnings: 0, orderCount: 0 };
      g.totalEarnings += o.totalEarnings;
      g.orderCount += 1;
      grouped.set(key, g);
    }
    return [...grouped.values()].sort((a, b) => b.date - a.date);
  }, [orders, timeFilter]);

  const chartData = useMemo(() => {
    const data = entries.slice(0, MAX_BARS[timeFilter] || 30)
      .slice()
      .sort((a, b) => a.date - b.date)
      .map(e => ({
        label: timeFilter === 'Year' ? fmtMonth(e.date) : fmtDay(e.date),
        earnings: Number(e.totalEarnings.toFixed(2)),
      }));
    if (data.length) return data;
    // Zero-valued points covering the filter period so the chart still
    // renders a flat line + axes when there are no earnings yet.
    const now = new Date();
    const dates = [];
    if (timeFilter === 'Day') {
      dates.push(now);
    } else if (timeFilter === 'Week') {
      for (let i = 6; i >= 0; i -= 1) dates.push(new Date(now.getFullYear(), now.getMonth(), now.getDate() - i));
    } else if (timeFilter === 'Month') {
      const days = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
      for (let i = 0; i < days; i += 1) dates.push(new Date(now.getFullYear(), now.getMonth(), i + 1));
    } else {
      for (let i = 0; i < 12; i += 1) dates.push(new Date(now.getFullYear(), i, 1));
    }
    return dates.map(d => ({
      label: timeFilter === 'Year' ? fmtMonth(d) : fmtDay(d),
      earnings: 0,
    }));
  }, [entries, timeFilter]);

  const historyTitle = {
    Day: "Today's Earnings",
    Week: 'Last 7 Days',
    Month: 'This Month',
    Year: 'This Year',
  }[timeFilter];

  const breakdownTitle = timeFilter === 'Year' ? 'Monthly Breakdown' : 'Daily Breakdown';

  const body = (
    <>
      {!bare && <h3 className="font-bold text-gray-900">{historyTitle}</h3>}

      {/* Chart always renders — flat zero line + axes when empty (iOS parity) */}
      <div className="h-[300px] -mx-1">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
            <defs>
              <linearGradient id="driverEarningsGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#22C55E" stopOpacity={0.4} />
                <stop offset="100%" stopColor="#22C55E" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: '#6b7280' }}
              tickLine={false}
              axisLine={{ stroke: '#e5e7eb' }}
            />
            <YAxis
              tick={{ fontSize: 11, fill: '#6b7280' }}
              tickLine={false}
              axisLine={false}
              tickFormatter={v => `$${v}`}
            />
            <Tooltip
              formatter={v => [formatCurrency(v), 'Earnings']}
              cursor={{ stroke: '#9CA3AF', strokeWidth: 1 }}
            />
            <Area
              type="monotone" dataKey="earnings"
              stroke="#22C55E" strokeWidth={3} strokeLinecap="round"
              fill="url(#driverEarningsGrad)"
              dot={({ cx, cy, index }) => (
                index === chartData.length - 1 && cx != null ? (
                  <g key={index}>
                    <circle cx={cx} cy={cy} r={4} fill="#22C55E" opacity={0.35}>
                      <animate attributeName="r" values="4;11;4" dur="1.8s" repeatCount="indefinite" />
                      <animate attributeName="opacity" values="0.45;0;0.45" dur="1.8s" repeatCount="indefinite" />
                    </circle>
                    <circle cx={cx} cy={cy} r={4} fill="#22C55E" stroke="#fff" strokeWidth={1.5} />
                  </g>
                ) : <g key={index} />
              )}
              activeDot={{ r: 6, fill: '#22C55E', stroke: '#fff', strokeWidth: 2 }}
              isAnimationActive animationDuration={900} animationEasing="ease-out"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {entries.length === 0 ? (
        <p className="text-sm text-gray-400 text-center">
          No earnings data for this period yet — deliveries will appear here.
        </p>
      ) : (
        <div className="space-y-2">
            <h4 className="text-sm font-semibold text-gray-700">{breakdownTitle}</h4>
            {entries.slice(0, 30).map(e => (
              <div
                key={e.date.getTime()}
                className="flex items-center justify-between px-3 py-2 rounded-lg bg-gray-50"
              >
                <div>
                  <p className="text-sm font-medium text-gray-800">
                    {timeFilter === 'Year'
                      ? fmtMonthYear(e.date)
                      : e.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </p>
                  <p className="text-[11px] text-gray-400">
                    {timeFilter === 'Year' ? 'Monthly Total' : dayOfWeek(e.date)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-green-600">{formatCurrency(e.totalEarnings)}</p>
                  <p className="text-[11px] text-gray-400">
                    {e.orderCount} {e.orderCount === 1 ? 'delivery' : 'deliveries'}
                  </p>
                </div>
              </div>
            ))}
            {entries.length > 30 && (
              <p className="text-[11px] text-gray-400 px-3">
                Showing 30 of {entries.length} entries
              </p>
            )}
        </div>
      )}
    </>
  );

  if (bare) return <div className="space-y-4">{body}</div>;
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 space-y-4">
      {body}
    </div>
  );
}
